import { PhotoSubjectType } from '@common/interfaces/photo'
import api from '../../../api'

/**
 * Resolved photos, keyed by `subjectType:id`, for the life of the page.
 *
 * Holds the in-flight promise rather than the settled value so the fifty rows of a list that mount
 * at once share a single request instead of racing fifty identical ones. Negative results are
 * cached too — a subject with no photo costs one request per session, not one per render.
 *
 * Generalizes the cache that lived inside Messages/MemberAvatar for users alone.
 */
const photoCache = new Map<string, Promise<string | null>>()

const cacheKey = (subjectType: PhotoSubjectType, id: string) => `${subjectType}:${id}`

const fetchPhoto = async (subjectType: PhotoSubjectType, id: string): Promise<string | null> => {
  // `apiClient` already unwraps the response body's `data`, so the service's `{ data: { photo } }`
  // arrives here as `{ photo }` — reading `.data.photo` resolves every photo to null and renders
  // initials for everyone.
  const response = (await api.photo.show(subjectType, id)) as { photo?: string | null }
  return response?.photo ?? null
}

export const loadPhoto = (subjectType: PhotoSubjectType, id: string): Promise<string | null> => {
  const key = cacheKey(subjectType, id)
  const cached = photoCache.get(key)
  if (cached !== undefined) return cached

  // Exactly one second chance, then fall back to initials: a lookup that fails twice isn't going
  // to start working, and the row renders fine either way. The failure is dropped from the cache
  // so a later mount can try again rather than being written off as "no photo" for the session.
  const request = fetchPhoto(subjectType, id)
    .catch((): Promise<string | null> => fetchPhoto(subjectType, id))
    .catch((): string | null => {
      photoCache.delete(key)
      return null
    })

  photoCache.set(key, request)
  return request
}

/**
 * Publish a known photo (or its removal) into the cache without a request.
 *
 * Called after an upload or delete so every already-mounted avatar for that subject repaints
 * immediately. Without it the editor would show the new photo while the header, the list row and
 * the team rail kept serving the previous one out of this cache until a reload.
 */
export const primePhoto = (
  subjectType: PhotoSubjectType,
  id: string,
  photo: string | null,
): void => {
  photoCache.set(cacheKey(subjectType, id), Promise.resolve(photo))
  notify(cacheKey(subjectType, id))
}

/**
 * Subscribers re-render when a subject's photo is replaced. A plain module-level Map plus a
 * version counter is enough here — the alternative (threading the new photo up through every
 * page that renders an avatar) is what left the five existing upload sites each managing their
 * own local `savedProfilePic` state.
 */
const listeners = new Map<string, Set<() => void>>()

const notify = (key: string): void => {
  listeners.get(key)?.forEach((listener) => listener())
}

export const subscribeToPhoto = (
  subjectType: PhotoSubjectType,
  id: string,
  listener: () => void,
): (() => void) => {
  const key = cacheKey(subjectType, id)
  const set = listeners.get(key) ?? new Set<() => void>()
  set.add(listener)
  listeners.set(key, set)

  return () => {
    set.delete(listener)
    if (!set.size) listeners.delete(key)
  }
}
